import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { useApiClient } from '../hooks/useApiClient';

// Analysis slots (analysis.analysis_type_id). 1 is the published tips; the rest are
// staff-only. 9 is the admin's swap buffer and never holds tips to show.
export const ANALYSIS_TYPES = {
  CONSERVATIVE: 1,
  VALUE: 2,
  TEST: 3,
  SLOT_4: 4,
  AUTO_REVIEW: 5,
} as const;

export type AnalysisTypeId = typeof ANALYSIS_TYPES[keyof typeof ANALYSIS_TYPES];

export interface AnalysisType {
  id: AnalysisTypeId;
  /** Short label for the model switch (Live, 2, 3, 4, AR), so it fits a race header. */
  name: string;
  /** Full name, for tooltips and screen readers. */
  title: string;
  description: string;
}

export const ANALYSIS_TYPE_OPTIONS: AnalysisType[] = [
  {
    id: ANALYSIS_TYPES.CONSERVATIVE,
    name: 'Live',
    title: 'Live',
    description: 'Conservative analysis with proven strategies'
  },
  {
    id: ANALYSIS_TYPES.VALUE,
    name: '2',
    title: 'Slot 2 (Alt)',
    description: 'Value-based analysis for higher returns'
  },
  {
    id: ANALYSIS_TYPES.TEST,
    name: '3',
    title: 'Slot 3 (Test)',
    description: 'Test analysis approach'
  },
  {
    id: ANALYSIS_TYPES.SLOT_4,
    name: '4',
    title: 'Slot 4',
    description: 'Analysis slot 4'
  },
  {
    id: ANALYSIS_TYPES.AUTO_REVIEW,
    name: 'AR',
    title: 'Auto Review',
    description: 'Auto review suggestions'
  }
];

export interface UserFeatures {
  analysis_type_value: boolean;
  analysis_type_selection: boolean;
  paywall_test_user: boolean;
  tipper_performance_card: boolean;
}

const isKnownAnalysisType = (typeId: unknown): typeId is AnalysisTypeId =>
  typeof typeId === 'number' && Object.values(ANALYSIS_TYPES).includes(typeId as AnalysisTypeId);

/**
 * May these features view this slot? Slot 1 (the published tips) is open to everyone;
 * every other slot is staff-only and needs both model switch features, the same rule
 * the API enforces (it serves slot 1 instead).
 */
export const isAnalysisTypeAllowed = (
  typeId: unknown,
  features: Pick<UserFeatures, 'analysis_type_value' | 'analysis_type_selection'>
): typeId is AnalysisTypeId => {
  if (!isKnownAnalysisType(typeId)) return false;
  if (typeId === ANALYSIS_TYPES.CONSERVATIVE) return true;
  return features.analysis_type_value && features.analysis_type_selection;
};

interface AnalysisTypeContextType {
  currentAnalysisType: AnalysisTypeId;
  availableAnalysisTypes: AnalysisType[];
  canSwitchAnalysisType: boolean;
  hasValueAccess: boolean;
  isPaywallTestUser: boolean;
  hasTipperPerformanceCard: boolean;
  /** The track_stats user feature; null until it has been checked for the signed-in user. */
  hasTrackStats: boolean | null;
  /** The multis user feature (the Multis page); null until it has been checked for the signed-in user. */
  hasMultis: boolean | null;
  /** Build your own and My multis (the multi_builder user feature); null while it is still being checked. */
  hasMultiBuilder: boolean | null;
  /** The customer_chat user feature (chat with the team); null until it has been checked for the signed-in user. */
  hasChat: boolean | null;
  isLoading: boolean;
  error: string | null;
  setAnalysisType: (typeId: AnalysisTypeId) => void;
  refreshUserFeatures: () => Promise<void>;
}

const AnalysisTypeContext = createContext<AnalysisTypeContextType | undefined>(undefined);

interface AnalysisTypeProviderProps {
  children: ReactNode;
}

const NO_FEATURES: UserFeatures = {
  analysis_type_value: false,
  analysis_type_selection: false,
  paywall_test_user: false,
  tipper_performance_card: false,
};

/**
 * The saved slot, if these features allow it; slot 1 otherwise. A known slot the
 * features do not (yet) allow stays saved, so it is honoured once a staff user's
 * features have loaded; anything unreadable is replaced with slot 1.
 */
export const getStoredAnalysisType = (
  features: Pick<UserFeatures, 'analysis_type_value' | 'analysis_type_selection'>
): AnalysisTypeId => {
  try {
    const savedType = localStorage.getItem('selectedAnalysisType');
    if (savedType && savedType.trim() !== '' && savedType !== '""') {
      const typeId = parseInt(savedType, 10);
      if (isKnownAnalysisType(typeId)) {
        return isAnalysisTypeAllowed(typeId, features) ? typeId : ANALYSIS_TYPES.CONSERVATIVE;
      }
    }
  } catch (error) {
    console.error('Error reading analysis type from localStorage:', error);
  }

  // Default to conservative if no valid saved value
  localStorage.setItem('selectedAnalysisType', ANALYSIS_TYPES.CONSERVATIVE.toString());
  return ANALYSIS_TYPES.CONSERVATIVE;
};

export const AnalysisTypeProvider: React.FC<AnalysisTypeProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const { client } = useApiClient();
  
  // Slot 1 until the user's features have loaded: only then can a saved staff-only slot be honoured
  const [currentAnalysisType, setCurrentAnalysisType] = useState<AnalysisTypeId>(() => getStoredAnalysisType(NO_FEATURES));
  const [userFeatures, setUserFeatures] = useState<UserFeatures>(NO_FEATURES);
  // Whose answer it is, so a stale "no" from before sign-in never reads as this user's
  const [pageAccess, setPageAccess] = useState<{ userId: string; trackStats: boolean; multis: boolean; multiBuilder: boolean; chat: boolean } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch user features when user changes
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      fetchUserFeatures();
    } else {
      // Reset to conservative when not authenticated
      setCurrentAnalysisType(ANALYSIS_TYPES.CONSERVATIVE);
      setUserFeatures(NO_FEATURES);
    }
  }, [isAuthenticated, user?.id, client]);

  const fetchUserFeatures = async () => {
    if (!client || !user?.id) return;

    try {
      setIsLoading(true);
      setError(null);

      const query = `
        query GetMyFeatures {
          hasValueAccess: myHasFeature(featureKey: "analysis_type_value")
          hasSelectionAccess: myHasFeature(featureKey: "analysis_type_selection")
          isPaywallTestUser: myHasFeature(featureKey: "paywall_test_user")
          hasTipperPerformanceCard: myHasFeature(featureKey: "tipper_performance_card")
          hasTrackStats: myHasFeature(featureKey: "track_stats")
          hasMultis: myHasFeature(featureKey: "multis")
          hasMultiBuilder: myHasFeature(featureKey: "multi_builder")
          hasChat: myHasFeature(featureKey: "customer_chat")
        }
      `;

      const response = await client.graphql({
        query
      });

      const data = response.data as any;
      
      const features: UserFeatures = {
        analysis_type_value: data.hasValueAccess || false,
        analysis_type_selection: data.hasSelectionAccess || false,
        paywall_test_user: data.isPaywallTestUser || false,
        tipper_performance_card: data.hasTipperPerformanceCard || false,
      };

      setUserFeatures(features);
      setPageAccess({ userId: user.id, trackStats: !!data.hasTrackStats, multis: !!data.hasMultis, multiBuilder: !!data.hasMultiBuilder, chat: !!data.hasChat });

      // Re-check the saved slot now the features are known: staff get it back, anyone else gets slot 1
      setCurrentAnalysisType(getStoredAnalysisType(features));

    } catch (err: any) {
      console.error('Error fetching user features:', err);
      setError(err.message || 'Failed to load user features');
      setPageAccess({ userId: user.id, trackStats: false, multis: false, multiBuilder: false, chat: false });
      
      // On error, assume no access to value features
      setUserFeatures(NO_FEATURES);
      setCurrentAnalysisType(ANALYSIS_TYPES.CONSERVATIVE);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshUserFeatures = async () => {
    await fetchUserFeatures();
  };

  const setAnalysisType = (typeId: AnalysisTypeId) => {
    console.log('setAnalysisType called with:', typeId, 'type:', typeof typeId);
    
    // Only a slot the user's features allow; anything else falls back to slot 1
    if (isAnalysisTypeAllowed(typeId, userFeatures)) {
      setCurrentAnalysisType(typeId);
      localStorage.setItem('selectedAnalysisType', typeId.toString());
      console.log('Analysis type set to:', typeId);
    } else {
      console.warn('Analysis type not available to this user:', typeId);
      setCurrentAnalysisType(ANALYSIS_TYPES.CONSERVATIVE);
      localStorage.setItem('selectedAnalysisType', ANALYSIS_TYPES.CONSERVATIVE.toString());
    }
  };

  // Calculate available analysis types based on user features
  const availableAnalysisTypes = ANALYSIS_TYPE_OPTIONS.filter(type => isAnalysisTypeAllowed(type.id, userFeatures));

  // Only this user's answer counts: a stale one from before a sign-in reads as still checking
  const ownAccess = pageAccess && pageAccess.userId === user?.id ? pageAccess : null;

  const contextValue: AnalysisTypeContextType = {
    currentAnalysisType,
    availableAnalysisTypes,
    canSwitchAnalysisType: userFeatures.analysis_type_selection && availableAnalysisTypes.length > 1,
    hasValueAccess: userFeatures.analysis_type_value,
    isPaywallTestUser: userFeatures.paywall_test_user,
    hasTipperPerformanceCard: true, // Enabled for all users
    hasTrackStats: !isAuthenticated ? false : ownAccess ? ownAccess.trackStats : null,
    hasMultis: !isAuthenticated ? false : ownAccess ? ownAccess.multis : null,
    hasMultiBuilder: !isAuthenticated ? false : ownAccess ? ownAccess.multiBuilder : null,
    hasChat: !isAuthenticated ? false : ownAccess ? ownAccess.chat : null,
    isLoading,
    error,
    setAnalysisType,
    refreshUserFeatures,
  };

  return (
    <AnalysisTypeContext.Provider value={contextValue}>
      {children}
    </AnalysisTypeContext.Provider>
  );
};

export const useAnalysisType = () => {
  const context = useContext(AnalysisTypeContext);
  if (context === undefined) {
    throw new Error('useAnalysisType must be used within an AnalysisTypeProvider');
  }
  return context;
};

// Utility hook to get the current analysis type name
export const useCurrentAnalysisTypeName = () => {
  const { currentAnalysisType } = useAnalysisType();
  const analysisType = ANALYSIS_TYPE_OPTIONS.find(type => type.id === currentAnalysisType);
  return analysisType?.name || 'Live';
};
